import * as THREE from 'three';

/**
 * 头顶聊天气泡（CanvasTexture + Sprite）。
 *
 * 用 Sprite 是因为它天然 billboard（永远面向相机），不用每帧算朝向；
 * 材质用 `MeshBasicMaterial` 同族的 `SpriteMaterial`，不受光照影响，VR 里也清晰。
 *
 * 自动换行 + 淡出：显示 6 秒，最后 1 秒淡出。
 */
const CANVAS_W = 512;
const CANVAS_H = 160;
const SHOW_SECONDS = 6;
const FADE_SECONDS = 1;

export class ChatBubble {
  readonly sprite: THREE.Sprite;

  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly texture: THREE.CanvasTexture;
  private remaining = 0;
  private text = '';

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_W;
    this.canvas.height = CANVAS_H;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;

    const material = new THREE.SpriteMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
      depthTest: false,
    });
    this.sprite = new THREE.Sprite(material);
    this.sprite.name = 'chat-bubble';
    this.sprite.scale.set(0.72, 0.225, 1);
    this.sprite.visible = false;
    this.sprite.renderOrder = 999;
  }

  /** 显示一条消息（会重置计时） */
  show(text: string): void {
    this.text = text.slice(0, 90);
    this.remaining = SHOW_SECONDS;
    this.draw(1);
    this.sprite.visible = true;
  }

  /** 每帧调用：淡出 + 隐藏 */
  update(dt: number): void {
    if (!this.sprite.visible) return;
    this.remaining -= dt;
    if (this.remaining <= 0) {
      this.sprite.visible = false;
      this.sprite.material.opacity = 1;
      return;
    }
    const opacity = this.remaining < FADE_SECONDS ? this.remaining / FADE_SECONDS : 1;
    if (opacity < 1) {
      this.sprite.material.opacity = opacity;
    }
  }

  /** 挂到某个对象上（一般是 avatar root），自动放到头顶 */
  attachTo(parent: THREE.Object3D, height: number): void {
    parent.add(this.sprite);
    this.sprite.position.set(0, height, 0);
  }

  private draw(alpha: number): void {
    const c = this.ctx;
    c.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // 气泡底
    c.globalAlpha = alpha;
    c.fillStyle = 'rgba(16, 26, 36, 0.92)';
    c.strokeStyle = 'rgba(124, 199, 224, 0.85)';
    c.lineWidth = 4;
    const pad = 6;
    c.beginPath();
    c.rect(pad, pad, CANVAS_W - pad * 2, CANVAS_H - pad * 2);
    c.fill();
    c.stroke();

    // 文字（自动换行）
    c.fillStyle = '#eaf4fa';
    c.font = '34px "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
    c.textAlign = 'center';
    c.textBaseline = 'middle';

    const maxWidth = CANVAS_W - 56;
    const lines: string[] = [];
    let current = '';
    for (const char of this.text) {
      const test = current + char;
      if (c.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = char;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);

    const lineHeight = 42;
    const startY = CANVAS_H / 2 - ((lines.length - 1) * lineHeight) / 2;
    lines.slice(0, 3).forEach((line, i) => {
      c.fillText(line, CANVAS_W / 2, startY + i * lineHeight);
    });

    c.globalAlpha = 1;
    this.texture.needsUpdate = true;
  }
}
